import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { plansAPI } from '@/utils/api';
import { openDocument } from '@/utils/documents';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, FileText, Upload, ExternalLink, Clock, CheckCircle2, XCircle } from 'lucide-react';

export default function PurchaseHistory() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const STATUS_CONFIG = {
    pending:  { label: t('purchaseHistory.statusPending'),  cls: 'bg-amber-100 text-amber-800',    icon: Clock },
    approved: { label: t('purchaseHistory.statusApproved'), cls: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
    rejected: { label: t('purchaseHistory.statusRejected'), cls: 'bg-red-100 text-red-800',         icon: XCircle },
    expired:  { label: t('purchaseHistory.statusExpired'),  cls: 'bg-gray-100 text-gray-800',       icon: Clock },
  };

  useEffect(() => {
    plansAPI.getMyPurchases()
      .then(r => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  // Both endpoints require a Bearer token — fetch as an authenticated blob
  // via the shared axios instance (auto-refreshes an expired access token
  // once) instead of a plain <a href> (which drops the Authorization header
  // and makes the browser download the 401 error body as a bogus file).
  const openReceipt = (id) => openDocument(`/purchases/${id}/receipt`, `recibo_${id.slice(0, 8)}.pdf`);

  const downloadInvoicePdf = (id, invoiceNumber) => {
    openDocument(`/purchases/${id}/invoice.pdf`, `Factura_${invoiceNumber}.pdf`);
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('pt-PT'); } catch { return '—'; }
  };

  const paymentMethodLabel = (m) => (
    m === 'vinti4' ? t('purchaseHistory.vinti4Card')
    : m === 'stripe' ? t('purchaseHistory.stripeCard')
    : m === 'bank_transfer' ? t('purchaseHistory.bankTransfer')
    : (m || t('purchaseHistory.payment'))
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />{t('purchaseHistory.back')}
        </Button>
        <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">{t('purchaseHistory.title')}</h1>
            <p className="text-gray-600 mt-1">{t('purchaseHistory.subtitle')}</p>
          </div>
          <Button onClick={() => navigate('/checkout')} className="bg-blue-600 hover:bg-blue-700" data-testid="history-buy-btn">
            {t('purchaseHistory.newPurchase')}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">{t('purchaseHistory.loading')}</div>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">{t('purchaseHistory.emptyTitle')}</h3>
            <p className="text-gray-600 mb-6">{t('purchaseHistory.emptyBody')}</p>
            <Button onClick={() => navigate('/checkout')} className="bg-blue-600 hover:bg-blue-700">{t('purchaseHistory.viewPlans')}</Button>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="purchases-list">
            {items.map((p) => {
              const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.pending;
              const Icon = cfg.icon;
              return (
                <Card key={p.id} className="p-5" data-testid={`purchase-row-${p.invoice_number}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-sm text-gray-500">#{p.invoice_number}</span>
                        <Badge className={cfg.cls}>
                          <Icon className="h-3 w-3 mr-1" />{cfg.label}
                        </Badge>
                      </div>
                      <div className="text-lg font-semibold mt-1">{p.plan_name}</div>
                      <div className="text-sm text-gray-600">
                        {paymentMethodLabel(p.payment_method)} · {formatDate(p.created_at)}
                      </div>
                      {p.status === 'pending' && p.payment_method === 'bank_transfer' && !p.proof_filename && (
                        <div className="text-sm text-amber-700 mt-2 flex items-center gap-1">
                          <Upload className="h-4 w-4" />{t('purchaseHistory.proofMissing')} —
                          <button className="underline font-medium" onClick={() => navigate(`/checkout?purchase=${p.id}`)}>
                            {t('purchaseHistory.sendNow')}
                          </button>
                        </div>
                      )}
                      {p.admin_note && (
                        <div className="text-sm text-gray-600 mt-1"><strong>{t('purchaseHistory.note')}:</strong> {p.admin_note}</div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">{p.amount_cve.toLocaleString('pt-PT')} <span className="text-sm text-gray-500">CVE</span></div>
                      <div className="flex flex-wrap gap-2 mt-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openReceipt(p.id)}
                          data-testid={`view-receipt-${p.invoice_number}`}
                        >
                          <FileText className="h-4 w-4 mr-1" />{t('purchaseHistory.view')}<ExternalLink className="h-3 w-3 ml-1" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadInvoicePdf(p.id, p.invoice_number)}
                          data-testid={`download-pdf-${p.invoice_number}`}
                        >
                          <FileText className="h-4 w-4 mr-1" />PDF
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
